/**
 * English is the reference dictionary: its shape defines `MessageKey`, so
 * every other locale is compile-checked against it. Adding a key here and
 * forgetting bn.ts is a type error, not a runtime "missing translation".
 */
export const en = {
  'app.name': 'Agrojatra',
  'app.tagline': 'Build habits. Track your study. Level yourself up.',

  // The first line says the record is safe, because that is the only thing
  // the reader actually wants to know.
  'crash.title': 'Something went wrong on this screen',
  'crash.safe': 'Your record is safe. Nothing you have logged was lost — only this screen failed to draw.',
  'crash.reload': 'Reload the app',
  'crash.back': 'Try this screen again',
  'crash.details': 'Technical details',

  // Navigation — four destinations and one action, per spec §47. Today carries
  // ~80% of sessions; the other three are deliberately places you rarely go.
  'nav.today': 'Today',
  'nav.jatra': 'Jatra',
  'nav.khata': 'Khata',
  'nav.me': 'Me',
  'nav.comingSoon': 'Coming soon',

  // The centre button. A *doing* menu, never a generic "add" — what is biggest
  // on screen is what the app is understood to be for.
  'now.open': 'Now',
  'now.title': 'Now',
  'now.startFocus': 'Start focus',
  'now.logEntry': 'Log what I did',
  'now.quickTask': 'Quick task',
  'now.newHabit': 'New habit',
  'now.newGoal': 'New goal',
  'now.soon': 'Soon',
  'now.close': 'Close',

  // Logging. "Subject" rather than "track" deliberately: the schema is
  // general so the app can serve someone learning guitar, but the words a
  // student reads should be the words a student uses. This is the default
  // vocabulary and becomes Goal-flavoured later, the same way ranks do.
  'log.subject': 'Subject',
  'log.newSubject': 'New',
  'log.subjectName': 'Subject name',
  'log.howLong': 'How long',
  'log.otherMinutes': 'Other',
  'log.minutesName': 'Minutes',
  'log.submit': 'Log it',
  'log.today': "Today's log",
  'log.nothingYet': 'Nothing logged yet today.',
  'log.moreEntries': '+{count} more',
  'log.timed': 'Timed',
  'log.review': 'Review',
  'log.entry': '{minutes} on {subject}',

  // Greetings key off the 4am day boundary, not midnight.
  'greeting.morning': 'Good morning',
  'greeting.afternoon': 'Good afternoon',
  'greeting.evening': 'Good evening',
  'greeting.night': 'Still up?',

  'home.today': 'Today',
  'home.addHabit': 'Add habit',
  'home.addTask': 'Add task',
  'home.empty.title': 'Nothing here yet',
  'home.empty.body': 'Add your first habit and start your streak today.',
  'home.habitName': 'Habit name',
  'home.tasks': 'Tasks',
  'home.taskName': 'Task name',
  'home.noTasks': 'Nothing on the list. Add what you plan to study today.',
  'home.openTasks': { one: '{count} left', other: '{count} left' },
  'home.overdue': 'Overdue',
  'home.moreTasks': '+{count} more',
  'home.daysLeft': '{count} days left',
  'goal.partsOf': '{done} of {total}',
  'home.streakAtRisk': "Your streak is still safe — one tick and it's locked in.",

  'streak.days': { one: '{count} day streak', other: '{count} day streak' },
  'streak.none': 'No streak yet',

  'power.label': 'Power',
  'power.level': 'Power Level',

  // Two registers for one ladder. The thresholds, the ids and the maths are
  // identical — only the words move. "Grandmaster" is a good name for a
  // seventeen-year-old sitting HSC and a slightly silly one for a thirty-
  // year-old training for a marathon, and "Novice" is worse than silly.
  'rank.student.novice': 'Novice',
  'rank.student.aspirant': 'Aspirant',
  'rank.student.scholar': 'Scholar',
  'rank.student.elite': 'Elite',
  'rank.student.master': 'Master',
  'rank.student.grandmaster': 'Grandmaster',
  'rank.student.legend': 'Legend',
  'rank.student.nationalTopper': 'National Topper',

  'rank.general.novice': 'Beginner',
  'rank.general.aspirant': 'Steady',
  'rank.general.scholar': 'Committed',
  'rank.general.elite': 'Seasoned',
  'rank.general.master': 'Practised',
  'rank.general.grandmaster': 'Disciplined',
  'rank.general.legend': 'Devoted',
  'rank.general.nationalTopper': 'Lifelong',

  'title.student.nightOwl': 'Night Owl',
  'title.student.earlyBird': 'Early Bird',
  'title.student.ironWill': 'Iron Will',
  'title.student.unbreakable': 'Unbreakable',
  'title.student.bookworm': 'Bookworm',
  'title.student.revisionMaster': 'Revision Master',
  'title.student.focusedMind': 'Focused Mind',
  'title.student.pomodoroPro': 'Pomodoro Pro',
  'title.student.marathonStudier': 'Marathon Studier',
  'title.student.phoenix': 'Phoenix',

  'title.general.nightOwl': 'Night Owl',
  'title.general.earlyBird': 'Early Riser',
  'title.general.ironWill': 'Iron Will',
  'title.general.unbreakable': 'Unbroken',
  'title.general.bookworm': 'Deep Worker',
  'title.general.revisionMaster': 'Reviser',
  'title.general.focusedMind': 'Focused',
  'title.general.pomodoroPro': 'Sprinter',
  'title.general.marathonStudier': 'Long Hauler',
  'title.general.phoenix': 'Comeback',

  // The one question onboarding asks about the person rather than the work.
  'coach.path.title': 'What are you working towards?',
  'coach.path.body':
    'One tap. It decides what we call things here, and what we can set up for you.',
  'path.hsc': 'HSC',
  'path.ssc': 'SSC',
  'path.admission': 'Admission',
  'path.university': 'University',
  'path.other': 'Something else',

  'goal.presets': 'Start from a ready list',

  'stats.studyToday': 'Studied today',
  'stats.studyTotal': 'Total study',
  'stats.achievements': { one: '{count} achievement', other: '{count} achievements' },
  'stats.habitsDone': '{done} of {total} done',

  'duration.hoursMinutes': '{hours}h {minutes}m',
  'duration.minutes': '{minutes}m',
  'duration.hours': '{hours}h',

  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.delete': 'Delete',
  'action.done': 'Done',
  'action.retry': 'Try again',
  'action.share': 'Share',
  'action.install': 'Install on phone',
  'action.add': 'Add',

  // Home — the habit list is the screen; everything else is chrome.
  'home.greetingWith': '{greeting}, {name}',
  'home.percent': '{value}%',
  // The proof-of-work line. Phrased as a fact that already happened, never as
  // a target to hit — "kept" is something you did, "87%" alone is a grade.
  'home.kept': {
    one: 'Kept {percent} of your promises in {count} day',
    other: 'Kept {percent} of your promises over {count} days',
  },
  'home.habits': 'Habits',
  'home.doneOfTotal': '{done} / {total}',
  // The handle is the whole reorder UI, so its label has to carry the
  // keyboard route as well — there is nothing else to discover.
  'home.dragHandle': 'Reorder {name} — hold and drag, or use the arrow keys',
  'home.template': 'Use the starter pack',
  'home.templateHint': 'Five habits most students keep. Added in one tap.',

  // The starter pack. A stand-in for the real template catalogue (§19) —
  // free, hardcoded, and enough to get a first tick inside a minute.
  'starter.read': 'Read for 30 minutes',
  'starter.math': 'Solve 10 maths problems',
  'starter.revise': "Revise today's class",
  'starter.sleep': 'Sleep before midnight',
  'starter.exercise': 'Exercise for 15 minutes',
  'home.newHabit': 'New habit',

  // The desktop wall chart. Every square says what it is out loud — colour
  // alone is not a label, least of all in one hue.
  'grid.floor': '{name}, {date}: floor kept',
  'grid.done': '{name}, {date}: done',
  'grid.missed': '{name}, {date}: missed',
  'grid.off': '{name}, {date}: not scheduled',
  'grid.future': '{name}, {date}: upcoming',

  'week.dayLabel': '{day}: {done} of {total} done',
  'week.dayEmpty': '{day}: nothing scheduled',

  'schedule.daily': 'Every day',
  'schedule.weekly': { one: '{count} day a week', other: '{count} days a week' },
  'schedule.timesPerWeek': { one: '{count}x a week', other: '{count}x a week' },

  'power.aria': 'Power Level {value}',
  'streak.aria': { one: '{count} day streak', other: '{count} day streak' },
  // Shown in place of the streak once it breaks. A broken run still happened,
  // and the app whose whole job is keeping evidence must not render it as 0.
  'streak.bestAria': { one: 'Best streak {count} day', other: 'Best streak {count} days' },

  'error.storage': "Couldn't open your saved data on this device.",

  'settings.theme': 'Theme',
  'me.finished': 'Finished',
  'me.lifetime': '{days} days recorded · {duration} · {kept} promises kept',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme.system': 'System',
  'settings.language': 'Language',

  // The Comeback (§45, §48 #3). The copy never names the streak that broke —
  // saying "you lost 12 days" is a loss notice, and loss notices are what the
  // 70%-in-100-days abandonment curve is made of.
  'comeback.title': 'Welcome back',
  'comeback.missed': {
    one: 'You missed a day.',
    other: 'You missed {count} days.',
  },
  'comeback.invite': 'Coming back is the hard part. Today is worth more than an ordinary one — one small thing is enough.',
  'comeback.madeTitle': 'You came back',
  'comeback.madeBody': 'That is the half almost everyone skips. Today earns more than an unbroken day.',

  // The floor — the two-minute version of a habit.
  'floor.action': 'Just {floor}',
  'floor.kept': 'Floor kept',
  'floor.label': 'The smallest version',
  'floor.hint': 'What you can still do on your worst day — "one page", "open the book".',
  'floor.optional': 'Optional',

  // Correcting an entry. `kind` is asked ONLY here — logging infers it, and
  // this is where a wrong inference gets put right.
  'log.kind': 'What was it',
  'log.kindLearn': 'First time',
  'log.kindReview': 'Revision',
  'log.save': 'Save',
  'log.delete': 'Delete',
  'log.edit': 'Edit {subject}, {minutes}',

  // খাতা — the ledger.
  'khata.title': 'Khata',
  'khata.allTracks': 'All',
  'khata.showEarlier': 'Show earlier',
  'khata.summary': '{duration} · {count} entries · mostly {track}',
  'khata.quietDays': {
    one: '· 1 quiet day ·',
    other: '· {count} quiet days ·',
  },
  'khata.trackTopic': '{track} · {topic}',
  'khata.outOf': '{value}/{total}',
  'khata.up': '↑ {value}',
  'khata.down': '↓ {value}',
  'khata.today': 'Today',
  'khata.yesterday': 'Yesterday',
  'khata.empty.title': 'Nothing written yet',
  'khata.empty.body': 'Every session you record lands here, oldest to newest. It is the evidence half of the day.',
  'khata.dayTotal': '{minutes}',

  // যাত্রা — who you are becoming. Not "stats": everything here either is an
  // identity or survives a bad day.
  'jatra.title': 'Jatra',
  'jatra.identity': 'Who you are becoming',
  'jatra.history': 'Your record',
  'jatra.keptTitle': 'Kept your word',
  'jatra.keptBody': '{kept} of {promised} promises over {days} days.',

  // The north-star metric (§51). Framed as a capability the student has, never
  // as a count of failures — the denominator is gaps, not days.
  'jatra.comebackTitle': 'Coming back',
  'jatra.comebackNone': "You haven't missed a day yet. When you do, this is where coming back gets counted.",
  'jatra.comebackRecord': {
    one: "You've come back {returns} time out of {gaps}.",
    other: "You've come back {returns} times out of {gaps}.",
  },
  'jatra.comebackLongest': {
    one: 'Your longest return was after {count} missed day.',
    other: 'Your longest return was after {count} missed days.',
  },
  'jatra.comebackAway': 'One more return and this goes up.',
  'jatra.streakBest': 'Longest run: {count} days',
  'jatra.empty.title': 'Nothing to show yet',
  'jatra.comebackOf': '{returns} of {gaps}',
  'jatra.empty.body': 'Add a habit on Today. Once there is a week behind you, this is where it adds up.',

  // The if-then plan (§48 #1). Written as a SENTENCE with slots, never as a
  // form with labels — the evidence is for implementation intentions in
  // if-then form, and a sentence is what makes the format survive.
  'plan.after': 'After',
  'plan.at': 'at',
  'plan.anchorName': 'what comes before',
  'plan.placeName': 'where',
  'plan.sentence': 'After {anchor}, at {place}',
  'plan.sentenceAnchorOnly': 'After {anchor}',
  'plan.sentencePlaceOnly': 'At {place}',

  // Anchors worth offering by default. Prayer times and school are the
  // load-bearing ones here: they already happen at the same point every day
  // without anyone having to remember them, which is exactly what an anchor
  // has to be.
  'anchor.fajr': 'Fajr',
  'anchor.school': 'school',
  'anchor.asr': 'Asr',
  'anchor.maghrib': 'Maghrib',
  'anchor.dinner': 'dinner',

  // The stat band around the wall chart (§53.6). Every one of these changes
  // what the student does next; none of them is a vanity total.
  'stats.kept': 'Kept',
  'stats.keptOf': '{kept} of {promised}',
  'stats.streak': 'Streak',
  'stats.streakDays': { one: '{count} day', other: '{count} days' },
  'stats.streakBest': 'Best {count}',
  'stats.comebacks': 'Comebacks',
  'stats.comebacksOf': '{returns} of {gaps}',
  'stats.comebacksNone': 'None needed yet',
  'stats.thisWeek': 'This week',
  'stats.vsLastWeek': '{delta} vs last week',
  'stats.sameAsLastWeek': 'Same as last week',
  'stats.firstWeek': 'First week',
  'stats.keptSoon': 'After 7 days',
  'stats.rowKept': '{value}% kept',

  // Measures — anything a student records a number for (§53.7).
  'measure.title': 'Results',
  'measure.add': 'Record a result',
  'measure.name': 'What is it',
  'measure.namePlaceholder': 'Physics mock, Class rank, Pages',
  'measure.value': 'Result',
  'measure.outOf': 'out of',
  'measure.track': 'Subject',
  'measure.unit': 'Unit',
  'measure.unitPlaceholder': 'pages, hours, kg',
  'measure.shape': 'How is it counted',
  'measure.shapeOutOf': 'A score',
  'measure.shapeRank': 'A position',
  'measure.shapeCount': 'A plain number',
  'measure.shapeOutOfHint': 'Out of a total — 62 of 80.',
  'measure.shapeRankHint': 'Where you placed — 12th of 340.',
  'measure.shapeCountHint': 'Just the number — 45 pages.',
  'measure.save': 'Record it',
  'measure.trendLabel': '{name} over time',
  'measure.valueOutOf': '{value} of {outOf}',
  'measure.valueRank': '{value} of {outOf}',
  'measure.deltaPoints': { one: '{count} point', other: '{count} points' },
  'measure.deltaPlaces': { one: '{count} place', other: '{count} places' },
  'measure.deltaFirst': 'First one recorded',
  'measure.deltaSame': 'Same as last time',
  'measure.readingOn': '{value} on {date}',
  'measure.empty.title': 'No results yet',
  'measure.empty.body': 'Record a test score, a class position, or anything else you count. This is the only place the app will say a subject is improving — because it is the only place with evidence.',
  'measure.attach': 'Add a result',
  'measure.attachHint': 'Got a mark back for this session?',

  // Focus — an action, never a place (§47). While one runs the tab bar is gone.
  'focus.title': 'Focus',
  'focus.setup': 'What are you working on',
  'focus.intention': 'What exactly',
  'focus.intentionPlaceholder': 'ch.4 problems, past paper Q1-10',
  'focus.intentionHint': 'A block with no stated aim becomes tab-switching with a timer running.',
  'focus.howLong': 'For how long',
  'focus.begin': 'Start',
  'focus.pause': 'Pause',
  'focus.resume': 'Resume',
  'focus.done': "I'm done",
  'focus.give': 'Stop without recording',
  'focus.paused': 'Paused',
  'focus.over': 'Over your plan',
  'focus.elapsed': '{minutes} so far',
  'focus.confirmAbandon': 'Stop and record nothing?',
  'focus.keep': 'Keep going',

  // The two questions on the way out (§48 #7).
  'focus.wrapTitle': 'Before you close it',
  'focus.recall': 'What do you remember',
  'focus.recallPlaceholder': 'Three things, without looking',
  'focus.recallHint': 'Naming it now, unprompted, is what makes it survive the week.',
  'focus.nextStep': 'Where to start next time',
  'focus.nextStepPlaceholder': 'problem 7',
  'focus.nextStepHint': 'One line now saves ten minutes of "where was I" tomorrow.',
  'focus.save': 'Save it',
  'focus.skip': 'Skip',
  'focus.recorded': '{minutes} recorded',

  // The review scheduler (§48 #10) — the one line that makes spaced retrieval
  // happen without anyone opting into a system.
  'review.title': 'Worth going back over',
  'review.oneTrack': '{track} — {topics}',
  'review.estimate': 'about {minutes}',
  'review.start': 'Revise now',
  'review.more': '+{count} more waiting',
  'review.overdue': { one: '{count} day late', other: '{count} days late' },
  'review.today': 'Due today',
  'review.done': 'Marked as revised',

  // The year at a glance, below the chart on Jatra.
  'year.title': 'This year',
  'year.legendLow': 'Less',
  'year.legendHigh': 'More',
  'year.perfect': { one: '{count} day fully kept', other: '{count} days fully kept' },
  'year.cell': '{date}: {kept} of {promised} kept',
  'year.cellRest': '{date}: nothing scheduled',
  'year.cellMissed': '{date}: nothing kept',

  // The daily line above the wall chart.
  'grid.dayLine': 'Share of each day kept',
  'grid.dayRatio': '{date}: {value}% of the day kept',
  'grid.dayRest': '{date}: nothing scheduled',
  'grid.dayToday': '{date}: today, still open',

  // ── Coach: onboarding and tips, taught on the live UI ───────────────────
  'coach.next': 'Next',
  'coach.done': 'Done',
  'coach.skip': 'Skip',
  'coach.notNow': 'Not now',
  'coach.welcome.title': 'Welcome to Agrojatra',
  'coach.goal.title': 'Start with where you are going',
  'coach.goal.body':
    'A habit with no destination is a chore. Name the thing you are working toward first.',
  'coach.welcome.body':
    'You make a promise, you keep it, and the record proves it. Let me show you in about a minute.',
  'coach.addHabit.body': 'Start with one thing you will actually do tomorrow. Small is fine.',
  'coach.promise.title': 'This is your promise',
  'coach.promise.body':
    'Tick the circle once you have done it — never before. The record is only worth something if it is true.',
  'coach.now.title': 'When you sit down',
  'coach.now.body': 'Start here to time a session or write down what you studied.',
  'coach.obstacle.title': 'What got in the way?',
  'coach.obstacle.body':
    'You missed a day — that is normal, and knowing why is what makes next week easier to plan.',
  'coach.obstacle.phone': 'Phone',
  'coach.obstacle.tired': 'Too tired',
  'coach.obstacle.busy': 'No time',
  'coach.obstacle.unclear': 'Did not know where to start',
  'coach.obstacle.placeholder': 'Something else…',
  'coach.keptRate.title': 'You kept your word',
  'coach.keptRate.body':
    'The share of your promises you actually kept. Unlike a streak, one bad day does not erase it.',

  // ── Goals ───────────────────────────────────────────────────────────────
  'goal.whatToward': 'What are you working toward?',
  'goal.titlePlaceholder': 'HSC 2027 — Mathematics',
  'goal.byWhen': 'By when?',
  'goal.in3Months': '3 months',
  'goal.in6Months': '6 months',
  'goal.inAYear': 'A year',
  'goal.pickDate': 'Pick a date',
  'goal.start': 'Start',
  'goal.setDestination': 'Set where you are going',
  'goal.pace': 'about one part every {days} days',
  'goal.whenHard': 'When it gets hard: {obstacle}',
  'goal.nextUpTitle': 'Next up',
  'goal.noParts': 'No parts yet. Breaking this into pieces is what makes progress visible.',
  'goal.addParts': 'Break it down',
  'goal.partsPlaceholder': 'One per line —\nch.1 Vectors\nch.2 Motion',
  'goal.showAll': 'Show all {count} left',
  'goal.showLess': 'Show fewer',
  'jatra.pathTitle': 'How far you have come',
  'jatra.pathNext': '{count} to {rank}',
  'jatra.pathEnd': 'The end of the road, and you walked it.',
  'jatra.alongTheWay': 'Along the way',
  'jatra.goalEmpty': 'No destination yet.',
  'jatra.setGoal': 'Set where you are going',
  'jatra.recordTitle': 'Your record',
  'weekly.title': 'Your week',
  'weekly.invite': 'You kept {kept} of {promised}. Two minutes to look back?',
  'weekly.start': 'Start review',
  'weekly.kept': '{kept} of {promised} kept',
  'weekly.up': 'up {value} points on last week',
  'weekly.down': 'down {value} points on last week',
  'weekly.slipped': '{name} slipped — {kept} of {promised}. Worth a minute of thought.',
  'weekly.whatGotInTheWay': 'What got in the way?',
  'weekly.lastTime': 'Last time you said: {obstacle}',
  'weekly.finish': 'Done',
  'jatra.trendsTitle': 'Results',
  'goal.cat.academic': 'Study',
  'goal.cat.career': 'Career',
  'goal.cat.skill': 'Skill',
  'goal.cat.fitness': 'Fitness',
  'goal.cat.business': 'Business',
  'goal.cat.creative': 'Creative',
  'goal.cat.personal': 'Personal',
  'coach.plan.title': 'When do you usually do this?',
  'coach.plan.body':
    'You have been doing {name} around {time}. Making that the plan is what turns it from a hope into a habit.',
  'coach.plan.confirm': 'Yes, {time}',
  'coach.floor.title': 'What is the smallest version?',
  'coach.floor.body':
    'You missed {name} yesterday. Name the two-minute version and a bad day can still be a kept day.',
  'coach.floor.placeholder': 'One page, open the book…',
} as const;

export type Dictionary = {
  [K in keyof typeof en]: (typeof en)[K] extends string
    ? string
    : { other: string; one?: string; few?: string; many?: string; zero?: string; two?: string };
};

export type MessageKey = keyof typeof en;
